/**
 * Device-contacts source for the bulk import: name + phone pairs pulled either
 * from the native contacts book (Capacitor plugin, needs READ_CONTACTS /
 * NSContactsUsageDescription in the shells) or from the web Contact Picker API
 * (Chrome on Android). Nothing leaves the device until the driver confirms the
 * import, same as the spreadsheet path.
 */
import { isNative } from "@/lib/capacitor";

export interface PickedContact {
  name: string;
  phone: string;
}

/**
 * "picked": the platform UI already let the driver multi-select (web Contact
 * Picker) — use the list as-is. "all": the whole address book was read (native
 * plugin has no multi-pick UI) — show our own selection dialog.
 */
export type ContactsPickResult =
  | { mode: "picked"; contacts: PickedContact[] }
  | { mode: "all"; contacts: PickedContact[] };

/** Thrown when the driver denied the contacts permission. */
export class ContactsPermissionError extends Error {
  constructor() {
    super("contacts-permission-denied");
    this.name = "ContactsPermissionError";
  }
}

// Contact Picker API (not yet in lib.dom): only Chromium on Android ships it.
interface WebContactProperties {
  name?: string[];
  tel?: string[];
}
interface WebContactsManager {
  select(
    properties: string[],
    options?: { multiple?: boolean },
  ): Promise<WebContactProperties[]>;
}

function webContactsManager(): WebContactsManager | null {
  const contacts = (navigator as Navigator & { contacts?: WebContactsManager })
    .contacts;
  return contacts && typeof contacts.select === "function" ? contacts : null;
}

/** Whether this device can provide contacts at all (gates the UI entry). */
export function contactsSourceAvailable(): boolean {
  return isNative() || webContactsManager() !== null;
}

function cleanPhone(raw: string): string {
  return raw.replace(/[\s()-]/g, "").trim();
}

/** Drops entries without a phone and collapses duplicate numbers. */
function normalize(contacts: PickedContact[]): PickedContact[] {
  const seen = new Set<string>();
  const out: PickedContact[] = [];
  for (const contact of contacts) {
    const phone = cleanPhone(contact.phone);
    if (!phone || seen.has(phone)) continue;
    seen.add(phone);
    out.push({ name: contact.name.trim(), phone });
  }
  return out;
}

// One-shot hand-off: the new-delivery page runs the picking flow, stashes the
// final selection here, and navigates to the import page which consumes it.
let stashedContacts: PickedContact[] | null = null;

export function stashContacts(contacts: PickedContact[]): void {
  stashedContacts = contacts;
}

export function takeStashedContacts(): PickedContact[] | null {
  const contacts = stashedContacts;
  stashedContacts = null;
  return contacts;
}

export async function pickDeviceContacts(): Promise<ContactsPickResult | null> {
  if (isNative()) {
    // Loaded on demand — dead weight for the pure-web session.
    const { Contacts } = await import("@capacitor-community/contacts");
    const permission = await Contacts.requestPermissions();
    if (permission.contacts !== "granted") {
      throw new ContactsPermissionError();
    }
    const { contacts } = await Contacts.getContacts({
      projection: { name: true, phones: true },
    });
    return {
      mode: "all",
      contacts: normalize(
        contacts.flatMap((contact) => {
          const name = contact.name?.display ?? "";
          return (contact.phones ?? []).map((phone) => ({
            name,
            phone: phone.number ?? "",
          }));
        }),
      ),
    };
  }

  const manager = webContactsManager();
  if (!manager) return null;
  // The browser shows its own multi-select UI; an empty result is a cancel.
  const picked = await manager.select(["name", "tel"], { multiple: true });
  if (picked.length === 0) return null;
  return {
    mode: "picked",
    contacts: normalize(
      picked.map((contact) => ({
        name: contact.name?.[0] ?? "",
        phone: contact.tel?.[0] ?? "",
      })),
    ),
  };
}
